<script lang="ts">
    import { spaLink, spaNavigate } from "../../functionTools/navigation"

    import { _ } from "svelte-i18n"
    import { getMyOrder } from "../../api"
    import { formatPriceCurrency, langField } from "../../i18n"
    import Index from "../SEO/Index.svelte"
    import BarLoader from "svelte-loading-spinners/BarLoader.svelte"
    import CartItemUserInputs from "../ecommerce/cart/CartItemUserInputs.svelte"
    import CartItemImage from "../ecommerce/cart/CartItemImage.svelte"
    import Icon from "../widgets/Icon.svelte"
    import { mdiStarCircleOutline } from "@mdi/js"

    interface Props {
        token: string
        id: string
    }

    let { token, id }: Props = $props()
</script>

<Index noIndex={true} title={$_("my-orders.order-details")} metaDescription={$_("meta.description")} />
<section id="myOrdersPage" class="section-type-1 section_padding checkout">
    <div class="container">
        <h1>{$_("my-orders.order-details")}</h1>
        {#await getMyOrder(token, id)}
            <BarLoader />
        {:then order}
            {#if order}
                <p>
                    {@html $_("my-orders.order-intro-html")}
                </p>
                <section class="navi">
                    <a href="/order/{token}" use:spaLink class="button secondary"
                        >&laquo; {$_("my-orders.back-to-orders")}</a
                    >
                    <a
                        use:spaLink
                        class="button primary"
                        href="/order/rating/{order.sequence}/{order.deliveryAddress.postcode}"
                        ><Icon path={mdiStarCircleOutline} />Bewertung Abgeben</a
                    >
                </section>
                <div class="order_details">
                    <section>
                        <div class="header">
                            <h3>
                                {$_("my-orders.order-from-date", {
                                    values: {
                                        date: new Date(order.insertTime).toLocaleDateString(),
                                    },
                                })}
                            </h3>
                            <div class="infotext">
                                {$_("my-orders.order-number-short")}:
                                {order.sequence}
                            </div>
                        </div>
                        <div class="price">
                            {$formatPriceCurrency(order.sum.totalGross)}
                        </div>
                    </section>

                    <section>
                        <div class="header">
                            <h3>{$_("my-orders.payment-info")}</h3>
                        </div>
                        <div class="text">
                            {#if order.payment.type == "paypal"}
                                Paypal
                            {:else if order.payment.type == "invoice"}
                                {$_("my-orders.invoice")}
                            {/if}
                        </div>
                    </section>

                    <section style="border-bottom:0;">
                        <div class="header">
                            <h3>{$_("my-orders.contact")}</h3>
                        </div>

                        <div class="innersection">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.email")}
                                    </div>
                                    {order.contact?.email || ""}
                                </div>
                                <div class="col-md-6"></div>
                            </div>
                        </div>

                        <div class="innersection">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.phone")}
                                    </div>
                                    {order.contact?.phone || ""}
                                </div>
                                <div class="col-md-6"></div>
                            </div>
                        </div>
                    </section>

                    <section style="border-bottom:0;">
                        <div class="header">
                            <h3>Lieferadresse</h3>
                        </div>
                        <div class="innersection">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.firstname")}
                                    </div>
                                    {order.deliveryAddress?.firstname || ""}
                                </div>
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.lastname")}
                                    </div>
                                    {order.deliveryAddress?.lastname || ""}
                                </div>
                            </div>
                        </div>

                        <div class="innersection">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.street-and-house-number")}
                                    </div>
                                    {order.deliveryAddress?.street || ""}
                                    {order.deliveryAddress?.houseNumber || ""}
                                </div>
                                <div class="col-md-6"></div>
                            </div>
                        </div>

                        <div class="innersection">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.postcode")}
                                    </div>
                                    {order.deliveryAddress?.postcode || ""}
                                </div>
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.city")}
                                    </div>
                                    {order.deliveryAddress?.city || ""}
                                </div>
                            </div>
                        </div>

                        <div class="innersection">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.country")}
                                    </div>
                                    {langField(order.deliveryCountry?.name, "")}
                                </div>
                                <div class="col-md-6"></div>
                            </div>
                        </div>
                    </section>

                    <section style="border-bottom:0;">
                        <div class="header">
                            <h3>Rechnungsadresse</h3>
                        </div>
                        <div class="innersection">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.firstname")}
                                    </div>
                                    {order.billingAddress?.firstname || ""}
                                </div>
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.lastname")}
                                    </div>
                                    {order.billingAddress?.lastname || ""}
                                </div>
                            </div>
                        </div>

                        <div class="innersection">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.street-and-house-number")}
                                    </div>
                                    {order.billingAddress?.street || ""}
                                    {order.billingAddress?.houseNumber || ""}
                                </div>
                                <div class="col-md-6"></div>
                            </div>
                        </div>

                        <div class="innersection">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.postcode")}
                                    </div>
                                    {order.billingAddress?.postcode || ""}
                                </div>
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.city")}
                                    </div>
                                    {order.billingAddress?.city || ""}
                                </div>
                            </div>
                        </div>

                        <div class="innersection">
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="description">
                                        {$_("my-orders.country")}
                                    </div>
                                    {order.billingAddress?.countryCode || ""}
                                </div>
                                <div class="col-md-6"></div>
                            </div>
                        </div>
                    </section>

                    <section style="border-bottom:0; padding-bottom:0;">
                        <div class="header">
                            <h3>{$_("my-orders.ordered-items")}</h3>
                        </div>

                        <div class="products">
                            {#each order.cart?.filter((item) => !item._parent_uuid) || [] as item}
                                <div class="product">
                                    <CartItemImage {item} useGridMobile={false} />
                                    <div class="text">
                                        <div class="headline">
                                            <h3>
                                                {langField(item.product?.name, "")}
                                            </h3>
                                            <div class="artnum">
                                                {$_("my-orders.item-number-short")}:
                                                {item.product?.code}
                                            </div>
                                        </div>
                                        <CartItemUserInputs cartItem={item} />
                                        <div class="bottom">
                                            <div class="count">
                                                {item.quantity}
                                                {$_("my-orders.pieces")}
                                            </div>
                                            <div class="price">
                                                {$formatPriceCurrency(item.price.unit)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            {/each}
                        </div>
                        {#if order.sum?.minQtyGross}
                            <div class="shipping_info">
                                <div class="infotext">
                                    {$_("my-orders.shipping-costs")}
                                </div>
                                <div class="price">
                                    {$formatPriceCurrency(order.sum?.minQtyGross)}
                                </div>
                            </div>
                        {/if}
                        {#if order.sum?.packagingGross}
                            <div class="shipping_info">
                                <div class="infotext">
                                    {$_("my-orders.packaging-costs")}
                                </div>
                                <div class="price">
                                    {$formatPriceCurrency(order.sum?.packagingGross)}
                                </div>
                            </div>
                        {/if}
                        {#if order.sum?.shippingGross}
                            <div class="shipping_info">
                                <div class="infotext">
                                    {$_("my-orders.shipping-costs")}
                                </div>
                                <div class="price">
                                    {$formatPriceCurrency(order.sum?.shippingGross)}
                                </div>
                            </div>
                        {/if}

                        <div class="endprice">
                            <div class="infotext">
                                {$_("my-orders.total-costs")}
                            </div>
                            <div class="price">
                                {$formatPriceCurrency(order.sum?.totalGross)}
                            </div>
                            <div class="vat">
                                {$_("my-orders.vat-included")}:
                                {$formatPriceCurrency(order.sum?.totalVAT)}
                            </div>
                        </div>
                    </section>
                </div>
            {:else}<b>{$_("my-orders.order-not-found")}</b><br /><br />{/if}
        {/await}

        <button
            onclick={() => {
                spaNavigate("/order/" + token)
            }}
            style="margin-top:60px;"
            class="button secondary">&laquo; {$_("my-orders.back-to-orders")}</button
        >
    </div>
</section>

<style lang="less">
    @import "../../css/variables.less";
    .navi {
        height: 30px;
        display: flex;
        justify-content: space-between;
        a {
            margin: 0px;
        }
    }
    h1 {
        margin: 0px;
    }
    .order_details {
        section {
            border-bottom: dotted 1px #ccc;
            padding: 40px 0px;
            .header {
                margin-bottom: 30px;
                h3 {
                    margin: 0px;
                }
                .infotext {
                    .flex();
                    font-size: 14px;
                }
            }
            .price {
                color: var(--primary);
                font-size: 1.8rem;
            }
            .innersection {
                font-weight: 800;
                border-bottom: dotted 1px #ccc;
            }
            .description {
                font-size: 14px;
                font-weight: 400;
            }
        }
        .products {
            .product {
                padding: 40px 0px;
                border-bottom: dotted 1px #ccc;
                .flex();
                flex-wrap: wrap;
                @media (max-width: 690px) {
                    flex-direction: column;
                }
                .text {
                    padding-left: 30px;
                    .flex();
                    flex-grow: 1;
                    flex-direction: column;
                    justify-content: space-between;
                    @media (max-width: 690px) {
                        flex-grow: unset;
                        width: 100%;
                        padding-left: 0px;
                    }
                    .artnum {
                        font-size: 14px;
                    }
                    .headline {
                        margin-bottom: 30px;
                    }
                    .bottom {
                        flex-grow: 1;
                        .flex();
                        justify-content: space-between;
                        align-items: flex-end;
                    }
                }
            }
        }
        .shipping_info {
            padding: 30px 0px;
            border-bottom: dotted 3px #ccc;
            .infotext {
                justify-content: flex-end;
                text-align: right;
            }
            text-align: right;
        }
        .endprice {
            padding: 30px 0px;
            .infotext {
                justify-content: flex-end;
                text-align: right;
            }
            text-align: right;
            .price {
                font-size: 2.5rem;
                font-weight: 800;
            }
        }
    }
</style>
